/**
 * WorkPlansList — control global de las tareas que hay que hacer en el parque.
 *
 * QUÉ ES UN PLAN
 * ---------------------------------------------------------------------------
 * Una tarea de campo que afecta a varios elementos («cifrar todo el parque»,
 * «preparar los cinco portátiles nuevos»). Puede caer sobre estaciones,
 * servidores, VMs, firewalls… y, si hace falta, solo sobre unos cuantos.
 *
 * PLANTILLAS
 * ---------------------------------------------------------------------------
 * Cualquier plan se puede guardar como plantilla (**Guardar como plantilla** en
 * su pantalla). Aquí se ven las del sistema, las que ha creado la empresa y se
 * pueden usar, renombrar o borrar. Crear un plan desde una plantilla es un clic.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  PlayIcon,
  PlusIcon,
  BookmarkIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import HelpDot from '../academy/HelpDot';
import api from '../services/api';
import './WorkPlans.css';

const STATUS_META = {
  active:   { label: 'En curso',  badge: 'list-badge--on' },
  draft:    { label: 'Borrador',  badge: 'list-badge--warn' },
  archived: { label: 'Archivado', badge: 'list-badge--off' },
};

const PLAN_VIEWS = [
  { key: 'list', label: 'Lista' },
  { key: 'card', label: 'Fichas' },
];

/** Tipos de equipo dentro de «equipos de rack» (RackElement.element_type). */
const RACK_TYPES = [
  { key: 'server', label: 'Servidores' },
  { key: 'switch', label: 'Switches' },
  { key: 'firewall', label: 'Firewalls' },
  { key: 'router', label: 'Routers' },
  { key: 'storage', label: 'Almacenamiento' },
  { key: 'pdu', label: 'PDUs' },
  { key: 'ups', label: 'SAI' },
  { key: 'panel', label: 'Patch panels' },
  { key: 'chassis', label: 'Chasis' },
];

/**
 * @param {object} props
 * @param {string|number} props.companyId
 * @param {string} [props.scopeType] Cuando se entra desde el módulo de un tipo de
 *   elemento (`workstation`), la lista se limita a los planes que aplican a ese
 *   tipo. No se esconde nada: el filtro se ve en el subtítulo y se puede salir
 *   al listado completo desde el menú.
 */
export default function WorkPlansList({ companyId, scopeType = null }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [plans, setPlans] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterObjective, setFilterObjective] = useState('');

  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState('');
  // Alcance inicial del plan en blanco: tipos de CI y, si entra rack_element,
  // qué tipos de equipo (servidores, switches…).
  const [blankTypes, setBlankTypes] = useState(['workstation']);
  const [blankRackTypes, setBlankRackTypes] = useState([]);
  // Condición de tipo de equipo: un plan de renove puede ser solo portátiles.
  const [blankDeviceType, setBlankDeviceType] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [editTpl, setEditTpl] = useState(null);
  const [delTpl, setDelTpl] = useState(null);

  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const cargar = useCallback(async () => {
    if (!companyId) { setPlans([]); setLoading(false); return; }
    setError('');
    try {
      const data = await api.getWorkPlans(companyId, filterStatus ? { status: filterStatus } : {});
      setPlans(data);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los planes');
    } finally {
      setLoading(false);
    }
  }, [companyId, filterStatus]);

  useEffect(() => { setLoading(true); cargar(); }, [cargar]);

  useEffect(() => {
    if (!companyId) return;
    api.getWorkPlanCatalog(companyId).then(setCatalogo).catch(() => setCatalogo([]));
  }, [companyId]);

  // Volver de la pantalla de un plan con «atrás» deja el listado como estaba.
  const planAbierto = searchParams.get('plan');
  useEffect(() => {
    if (planAbierto) {
      navigate(`/work-plans/${planAbierto}`);
      const copia = new URLSearchParams(searchParams);
      copia.delete('plan');
      setSearchParams(copia, { replace: true });
    }
  }, [planAbierto, navigate, searchParams, setSearchParams]);

  const cargarPlantillas = useCallback(async () => {
    if (!companyId) return;
    try {
      setTemplates(await api.getWorkPlanTemplates(companyId));
    } catch (e) {
      setDialogError(e.message || 'No se pudieron cargar las plantillas');
    }
  }, [companyId]);

  const abrirNuevo = async () => {
    setDialogError('');
    setBlankTypes(['workstation']);
    setBlankRackTypes([]);
    setBlankDeviceType('');
    setNuevoOpen(true);
    await cargarPlantillas();
  };

  const usarPlantilla = async (plantilla) => {
    setCreating(`tpl-${plantilla.id}`);
    setDialogError('');
    try {
      const plan = await api.createWorkPlan(companyId, { template_id: plantilla.id });
      setNuevoOpen(false);
      await cargar();
      if (plan?.id) navigate(`/work-plans/${plan.id}`);
    } catch (e) {
      setDialogError(e.message || 'No se pudo crear el plan');
    } finally {
      setCreating('');
    }
  };

  const crearEnBlanco = async () => {
    setCreating('blank');
    setDialogError('');
    try {
      const tipos = blankTypes.length ? blankTypes : ['workstation'];
      const plan = await api.createWorkPlan(companyId, {
        title: 'Plan de trabajo sin título',
        steps: [],
        target_types: tipos,
        target_filter: blankDeviceType && tipos.includes('workstation')
          ? { device_type: blankDeviceType }
          : {},
        target_rack_types: tipos.includes('rack_element') ? blankRackTypes : [],
      });
      setNuevoOpen(false);
      await cargar();
      if (plan?.id) navigate(`/work-plans/${plan.id}`);
    } catch (e) {
      setDialogError(e.message || 'No se pudo crear el plan');
    } finally {
      setCreating('');
    }
  };

  const guardarPlantilla = async () => {
    if (!editTpl) return;
    const titulo = (editTpl.title || '').trim();
    if (!titulo) { setDialogError('La plantilla necesita un título'); return; }
    try {
      await api.updateWorkPlanTemplate(companyId, editTpl.id, {
        title: titulo,
        description: editTpl.description,
        objective: editTpl.objective,
      });
      setEditTpl(null);
      await cargarPlantillas();
    } catch (e) {
      setDialogError(e.message || 'No se pudo guardar la plantilla');
    }
  };

  const borrarPlantilla = async () => {
    if (!delTpl) return;
    try {
      await api.deleteWorkPlanTemplate(companyId, delTpl.id);
      setDelTpl(null);
      await cargarPlantillas();
    } catch (e) {
      setDialogError(e.message || 'No se pudo borrar la plantilla');
    }
  };

  const guardarEdicion = async () => {
    if (!editing) return;
    setDialogError('');
    const titulo = (editing.title || '').trim();
    if (!titulo) { setDialogError('El plan necesita un título'); return; }
    try {
      await api.updateWorkPlan(companyId, editing.id, {
        title: titulo,
        description: editing.description,
        objective: editing.objective,
        status: editing.status,
      });
      setEditing(null);
      await cargar();
    } catch (e) {
      setDialogError(e.message || 'No se pudo guardar');
    }
  };

  const borrar = async () => {
    if (!deleting) return;
    try {
      await api.deleteWorkPlan(companyId, deleting.id);
      setDeleting(null);
      await cargar();
    } catch (e) {
      setDialogError(e.message || 'No se pudo borrar el plan');
    }
  };

  const recalcular = async (plan) => {
    setError('');
    try {
      await api.evaluateWorkPlan(companyId, plan.id);
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo recalcular');
    }
  };

  const labelTipo = useCallback(
    (tipo) => catalogo.find((c) => c.key === tipo)?.label || tipo,
    [catalogo],
  );

  const objectives = useMemo(
    () => Array.from(new Set(plans.map((p) => p.objective).filter(Boolean))).sort(),
    [plans],
  );

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans.filter((p) => {
      if (scopeType && !(p.target_types || []).includes(scopeType)) return false;
      if (filterObjective && p.objective !== filterObjective) return false;
      if (!q) return true;
      return `${p.title} ${p.description || ''} ${p.objective || ''}`.toLowerCase().includes(q);
    });
  }, [plans, search, filterObjective, scopeType]);

  const kpis = useMemo(() => {
    const activos = plans.filter((p) => p.status === 'active');
    const elementos = activos.reduce((n, p) => n + (p.progress?.elementos || 0), 0);
    const hechos = activos.reduce((n, p) => n + (p.progress?.hechos || 0), 0);
    const total = activos.reduce((n, p) => n + (p.progress?.total || 0), 0);
    const completos = activos.reduce((n, p) => n + (p.progress?.elementos_completos || 0), 0);
    return { planes: activos.length, elementos, hechos, total, completos };
  }, [plans]);

  const columns = useMemo(() => [
    {
      key: 'title',
      label: 'Plan',
      sortable: true,
      sortValue: (p) => p.title,
      render: (p) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.title}</div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
            {[p.objective, `${p.steps?.length || 0} pasos`].filter(Boolean).join(' · ')}
          </div>
        </div>
      ),
    },
    {
      key: 'targets',
      label: 'Aplica a',
      sortable: true,
      sortValue: (p) => (p.target_types || []).join(','),
      render: (p) => {
        const filtro = p.target_filter || {};
        const deviceType = String(filtro.device_type || '').toLowerCase();
        const tipoEquipo = deviceType
          ? (['laptop', 'portatil', 'portátil'].includes(deviceType) ? ' · Solo portátiles' : ' · Solo sobremesa')
          : '';
        const nCondiciones = (filtro.conditions || []).filter((c) => c?.field).length;
        return (
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {(p.target_types || []).map(labelTipo).join(' · ')}
            {tipoEquipo}
            {nCondiciones > 0 && (
              <span className="list-badge list-badge--warn" style={{ marginLeft: 6 }}>
                {nCondiciones} condiciones
              </span>
            )}
            {p.target_ids?.length ? (
              <span className="list-badge list-badge--warn" style={{ marginLeft: 6 }}>
                {p.target_ids.length} elegidos
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Estado',
      sortable: true,
      sortValue: (p) => p.status,
      render: (p) => {
        const meta = STATUS_META[p.status] || STATUS_META.draft;
        return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
      },
    },
    {
      key: 'elementos',
      label: 'Elementos',
      sortable: true,
      sortValue: (p) => p.progress?.elementos || 0,
      render: (p) => <span className="list-code">{p.progress?.elementos ?? 0}</span>,
    },
    {
      key: 'progress',
      label: 'Avance',
      sortable: true,
      sortValue: (p) => p.progress?.porcentaje || 0,
      render: (p) => {
        const pct = p.progress?.porcentaje ?? 0;
        return (
          <div className="wp-progress">
            <div className="wp-progress-bar">
              <div className={`wp-progress-fill ${pct === 100 ? 'is-done' : ''}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="wp-progress-text">
              {p.progress?.hechos ?? 0}/{p.progress?.total ?? 0}
            </span>
          </div>
        );
      },
    },
    {
      key: 'completos',
      label: 'Al día',
      sortable: true,
      sortValue: (p) => p.progress?.elementos_completos || 0,
      render: (p) => (
        <span>
          <span className="list-code">{p.progress?.elementos_completos ?? 0}</span>
          <span style={{ color: 'var(--text-muted)' }}> / {p.progress?.elementos ?? 0}</span>
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: 'Actualizado',
      sortable: true,
      sortValue: (p) => p.updated_at || '',
      render: (p) => (
        <span className="list-hint">
          {p.updated_at ? new Date(p.updated_at).toLocaleDateString('es-ES') : '—'}
        </span>
      ),
    },
  ], [labelTipo]);

  const filters = [
    {
      key: 'status',
      label: 'Estado',
      allLabel: 'Todos los estados',
      value: filterStatus,
      onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'objective',
      label: 'Objetivo',
      allLabel: 'Todos los objetivos',
      value: filterObjective,
      onChange: setFilterObjective,
      options: objectives.map((o) => ({ value: o, label: o })),
    },
  ];

  return (
    <>
      <ListPage
        icon={ClipboardDocumentCheckIcon}
        title="Planes de trabajo"
        subtitle={
          scopeType === 'workstation'
            ? `Solo los planes que aplican a estaciones de trabajo${
              kpis.planes ? ` · ${kpis.planes} en curso` : ''
            }`
            : (kpis.planes
              ? `${kpis.planes} planes en curso · ${kpis.elementos} elementos afectados · ${kpis.hechos}/${kpis.total} tareas hechas`
              : 'Tareas que hay que completar en los elementos, con su avance en todo el parque')
        }
        headerActions={(
          <>
            <HelpDot topic="planes.list" label="Ayuda: los planes de trabajo" />
            <button type="button" className="wp-btn" onClick={abrirNuevo} disabled={!companyId}>
              <PlusIcon /> Nuevo plan
            </button>
          </>
        )}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por título, descripción u objetivo…"
        filters={filters}
        exportRows={filtrados}
        exportFileBase="planes-de-trabajo"
        exportSheetName="Planes"

        columns={columns}
        columnStorageKey="cmdb_cols_work_plans"
        companyId={companyId}
        rows={filtrados}
        rowKey={(p) => p.id}
        onRowClick={(p) => navigate(`/work-plans/${p.id}`)}
        rowTitle={(p) => `Abrir el plan ${p.title}`}

        views={PLAN_VIEWS}
        listKey="work-plans"
        defaultView="list"

        loading={loading}
        loadingText="Cargando planes…"
        error={error}

        empty={{
          icon: '📋',
          title: plans.length === 0 ? 'Todavía no hay planes de trabajo' : 'Ningún plan coincide con los filtros',
          subtitle: plans.length === 0
            ? 'Crea uno a partir de una plantilla: altas, cifrado, memoria…'
            : undefined,
          action: plans.length === 0 && companyId ? (
            <button type="button" className="list-btn-primary" onClick={abrirNuevo}>
              ＋ Crear el primero
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (p) => (
            <>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => navigate(`/work-plans/${p.id}`)}
                title="Abrir el control global del plan"
              >
                <PlayIcon />
              </button>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => recalcular(p)}
                title="Recalcular el avance con el inventario actual"
              >
                <ArrowPathIcon />
              </button>
            </>
          ),
          onEdit: (p) => { setDialogError(''); setEditing({ ...p }); },
          onDelete: (p) => { setDialogError(''); setDeleting(p); },
          deleteTitle: 'Eliminar plan',
          deleteMessage: (p) => (
            <>
              ¿Seguro que quieres eliminar el plan <strong>{p.title}</strong>?
              {' '}Se borra también su histórico de pasos. Los elementos no se tocan.
            </>
          ),
        }}
      />

      {/* ── Nuevo plan ───────────────────────────────────────────────────── */}
      {nuevoOpen && (
        <Dialog
          title="Nuevo plan de trabajo"
          icon={<BookmarkIcon />}
          onClose={() => setNuevoOpen(false)}
          maxWidth={700}
          footer={(
            <button type="button" className="wp-btn" onClick={() => setNuevoOpen(false)}>
              Cancelar
            </button>
          )}
        >
          {dialogError && <div className="list-hint" style={{ color: 'var(--red-600, #dc2626)' }}>{dialogError}</div>}
          <p className="list-hint" style={{ marginTop: 0 }}>
            Elige una plantilla. Las de la casa vienen con los campos de inventario ya
            escritos; las que guardes tú salen de un plan que ya hayas montado.
          </p>
          <div className="wp-template-grid">
            {templates.map((t) => (
              <div key={t.id} className={`wp-template ${creating === `tpl-${t.id}` ? 'is-selected' : ''}`}>
                <button
                  type="button"
                  className="wp-template-body"
                  onClick={() => usarPlantilla(t)}
                  disabled={Boolean(creating)}
                >
                  <div className="wp-template-title">
                    {t.title}
                    {t.builtin && <span className="wp-tag">sistema</span>}
                  </div>
                  <div className="wp-template-desc">{t.description}</div>
                  <div className="wp-template-desc" style={{ marginTop: 6 }}>
                    {(t.steps || []).length} pasos · {(t.target_types || []).map(labelTipo).join(' · ')}
                  </div>
                </button>
                <div className="wp-template-actions">
                  <button
                    type="button"
                    className="wp-icon-btn"
                    title="Renombrar o describir de otra forma"
                    onClick={() => { setDialogError(''); setEditTpl({ ...t }); }}
                  >
                    <PencilSquareIcon />
                  </button>
                  <button
                    type="button"
                    className="wp-icon-btn"
                    title="Borrar la plantilla"
                    onClick={() => { setDialogError(''); setDelTpl(t); }}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}
            <div className="wp-template">
              <div className="wp-template-body" style={{ cursor: 'default' }}>
                <div className="wp-template-title">
                  Empezar en blanco
                  {creating === 'blank' && ' …'}
                </div>
                <div className="wp-template-desc">
                  Un plan vacío: eliges el alcance ahora y los pasos se añaden dentro.
                </div>
                <div className="list-hint" style={{ marginTop: 8, fontWeight: 600 }}>Aplica a</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                  {(catalogo.length ? catalogo : [
                    { key: 'workstation', label: 'Estaciones de trabajo' },
                    { key: 'rack_element', label: 'Equipos de rack' },
                  ]).map((c) => (
                    <label key={c.key} className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="checkbox"
                        checked={blankTypes.includes(c.key)}
                        onChange={(e) => setBlankTypes((prev) => (
                          e.target.checked ? [...prev, c.key] : prev.filter((k) => k !== c.key)
                        ))}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
                {blankTypes.includes('workstation') && (
                  <>
                    <div className="list-hint" style={{ marginTop: 8, fontWeight: 600 }}>
                      Tipo de equipo
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {[
                        { value: '', label: 'Todos' },
                        { value: 'laptop', label: 'Solo portátiles' },
                        { value: 'pc', label: 'Solo sobremesa' },
                      ].map((opcion) => (
                        <button
                          key={opcion.value || 'todos'}
                          type="button"
                          className={`wp-chip ${blankDeviceType === opcion.value ? 'is-on' : ''}`}
                          onClick={() => setBlankDeviceType(opcion.value)}
                        >
                          {opcion.label}
                        </button>
                      ))}
                    </div>
                    <div className="list-hint" style={{ marginTop: 4 }}>
                      Haz dos planes si quieres tareas distintas para portátiles y sobremesa;
                      con «Todos», un solo plan vale para ambos.
                    </div>
                  </>
                )}
                {blankTypes.includes('rack_element') && (
                  <>
                    <div className="list-hint" style={{ marginTop: 8, fontWeight: 600 }}>
                      Tipos de equipo de rack
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                      {RACK_TYPES.map((r) => (
                        <label key={r.key} className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={blankRackTypes.includes(r.key)}
                            onChange={(e) => setBlankRackTypes((prev) => (
                              e.target.checked ? [...prev, r.key] : prev.filter((k) => k !== r.key)
                            ))}
                          />
                          {r.label}
                        </label>
                      ))}
                    </div>
                    <div className="list-hint" style={{ marginTop: 4 }}>
                      Sin marcar ninguno = todos los tipos de rack.
                    </div>
                  </>
                )}
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="list-btn-primary"
                    onClick={crearEnBlanco}
                    disabled={Boolean(creating) || blankTypes.length === 0}
                  >
                    Crear plan en blanco
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="list-hint" style={{ marginTop: 12 }}>
            ¿Tienes ya un plan montado? Ábrelo y pulsa <strong>Guardar como plantilla</strong>.
          </div>
        </Dialog>
      )}

      {editTpl && (
        <Dialog
          title="Editar plantilla"
          onClose={() => setEditTpl(null)}
          maxWidth={520}
          footer={(
            <>
              <button type="button" className="wp-btn" onClick={() => setEditTpl(null)}>Cancelar</button>
              <button type="button" className="list-btn-primary" onClick={guardarPlantilla}>Guardar</button>
            </>
          )}
        >
          {dialogError && <div className="list-hint" style={{ color: 'var(--red-600, #dc2626)' }}>{dialogError}</div>}
          <label className="list-hint" htmlFor="wp-tpl-title">Título</label>
          <input
            id="wp-tpl-title"
            className="list-input"
            value={editTpl.title || ''}
            onChange={(e) => setEditTpl({ ...editTpl, title: e.target.value })}
            style={{ width: '100%', marginBottom: 10 }}
          />
          <label className="list-hint" htmlFor="wp-tpl-desc">Descripción</label>
          <textarea
            id="wp-tpl-desc"
            className="list-input"
            rows={3}
            value={editTpl.description || ''}
            onChange={(e) => setEditTpl({ ...editTpl, description: e.target.value })}
            style={{ width: '100%', marginBottom: 10 }}
          />
          <label className="list-hint" htmlFor="wp-tpl-obj">Objetivo</label>
          <input
            id="wp-tpl-obj"
            className="list-input"
            value={editTpl.objective || ''}
            onChange={(e) => setEditTpl({ ...editTpl, objective: e.target.value })}
            style={{ width: '100%' }}
          />
          <div className="list-hint" style={{ marginTop: 10 }}>
            Los pasos se editan en un plan: ábrelo, cámbialos y vuelve a guardarlo como
            plantilla.
          </div>
        </Dialog>
      )}

      {delTpl && (
        <Dialog
          title="Borrar plantilla"
          onClose={() => setDelTpl(null)}
          footer={(
            <>
              <button type="button" className="wp-btn" onClick={() => setDelTpl(null)}>Cancelar</button>
              <button type="button" className="list-btn-primary" onClick={borrarPlantilla}>Borrar</button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>
            ¿Borrar la plantilla <strong>{delTpl.title}</strong>? Los planes ya creados
            con ella no se tocan.
          </p>
        </Dialog>
      )}

      {/* ── Editar plan ──────────────────────────────────────────────────── */}
      {editing && (
        <Dialog
          title="Editar plan"
          onClose={() => setEditing(null)}
          maxWidth={520}
          footer={(
            <>
              <button type="button" className="wp-btn" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="button" className="list-btn-primary" onClick={guardarEdicion}>Guardar</button>
            </>
          )}
        >
          {dialogError && <div className="list-hint" style={{ color: 'var(--red-600, #dc2626)' }}>{dialogError}</div>}
          <label className="list-hint" htmlFor="wp-title">Título</label>
          <input
            id="wp-title"
            className="list-input"
            value={editing.title || ''}
            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            style={{ width: '100%', marginBottom: 10 }}
          />
          <label className="list-hint" htmlFor="wp-desc">Descripción</label>
          <textarea
            id="wp-desc"
            className="list-input"
            rows={3}
            value={editing.description || ''}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            style={{ width: '100%', marginBottom: 10 }}
          />
          <label className="list-hint" htmlFor="wp-objective">Objetivo</label>
          <input
            id="wp-objective"
            className="list-input"
            value={editing.objective || ''}
            onChange={(e) => setEditing({ ...editing, objective: e.target.value })}
            style={{ width: '100%', marginBottom: 10 }}
          />
          <label className="list-hint" htmlFor="wp-status">Estado</label>
          <select
            id="wp-status"
            className="list-input"
            value={editing.status || 'active'}
            onChange={(e) => setEditing({ ...editing, status: e.target.value })}
            style={{ width: '100%' }}
          >
            {Object.entries(STATUS_META).map(([value, meta]) => (
              <option key={value} value={value}>{meta.label}</option>
            ))}
          </select>
        </Dialog>
      )}

      {deleting && (
        <Dialog
          title="Eliminar plan"
          onClose={() => setDeleting(null)}
          footer={(
            <>
              <button type="button" className="wp-btn" onClick={() => setDeleting(null)}>Cancelar</button>
              <button type="button" className="list-btn-primary" onClick={borrar}>Eliminar</button>
            </>
          )}
        >
          {dialogError && <div className="list-hint" style={{ color: 'var(--red-600, #dc2626)' }}>{dialogError}</div>}
          <p style={{ margin: 0 }}>
            ¿Eliminar <strong>{deleting.title}</strong>? Se borra su histórico de pasos.
            Los elementos no se tocan.
          </p>
        </Dialog>
      )}
    </>
  );
}
